/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  VersionCompareMode,
  ParentEventHandler,
  SetStateArgs,
  SubscribableHubCommand,
  GetDefaultStateHandler,
  SubscribeToServiceEventsHandler,
  UnsubscribeFromServiceHandler,
  CallProcessorFuncHandler,
  HubBroadcastEvent,
  WidgetState,
  WidgetType ,
  DeepReadOnly,
  DeepReadOnlyArray ,
  HubServiceState,
  InitializeServiceInstanceHandler,
  TerminateServiceInstanceHandler
} from '@kemu-io/kemu-core/types';
import EventEmitter from 'emittery';
import KemuCore from '@kemu-io/kemu-core';
import { createImageDataFromInfo, isImageDataType } from '@kemu-io/kemu-core/common/utils';
import { findRecipe, findWidget, getRecipeContextInfo } from '@kemu-io/kemu-core/common/recipeCache';
import {
  GetServiceContentsArgs,
  GetServiceContentsResponse,
  InitInstanceArgs,
  TerminateInstanceArgs,
  SetOutputsArgs,
  KemuHubFunctions,
  SerializableServiceInfo,
  BroadcastEvent,
  TargetOutput,
  SetDependencyPathsArgs,
  GetCurrentDependencyPathsArgs,
  ServiceToServiceFunctions,
  DataType,
  ImageDataLike,
  Data,
  GetServicesResponse,
  HubSystemInfo,
  RecipeContextInfo,
  SendToRecipeArgs,
  WidgetSecretConfig,
  ParseStringExpressionArgs,
  ParseStringExpressionResponse,
  GetGlobalVariableNamesArgs,
  GetGlobalVariableNamesResponse,
  GetGlobalVariableValuesArgs,
  GetGlobalVariableValuesResponse,
  SetGlobalVariableValuesArgs,
  InitInstanceResponse,
  GetCurrentDependencyPathsResponse,
  RemoveDependencyPathsArgs,
} from '@kemu-io/hs-types';
import KemuRemoteInvoke, { ExecuteConfig, HandleRemoteInvokeFn, RemoveInvokeError } from '@kemu-io/hs/ri';
import { satisfies } from 'compare-versions';
import { ChildWidgetError } from '@kemu-io/kemu-core/processor';
import { renderTemplateAsync } from '@kemu-io/kemu-core/common/managers/stringExpressionsManager.js';
import kemuLink from './link';
import { onAckRequest, onServicesListChanged, rebuildKemuTypes } from './helpers';
import { HubConnectionStatus, HubConnectorWithWebAppMethods } from '@src/types/kemuHub/hub_t';
import createLogger from '@common/logger';
import { KemuComposerGuestServiceId, KemuHubServiceId } from '@common/constants';
import globalVariablesManager from '@kemu-io/kemu-core/common/managers/globalVariables.js';

type ConnectionEvent = 'connected' | 'acknowledged' | 'disconnected';
type ValidEvent = SubscribableHubCommand | ConnectionEvent;
type VoidCallback = (...args: any[]) => void;
type BasicUserInfo = {
  email?: string,
  id: string,
  // apiKey: string,
}


type AckResponseArgs = {
  ackSessionId: number;
  // /** @deprecated use `user.apiKey` instead. Provided for backwards compatibility with old hub versions */
  // apiKey?: string;
  recipe: RecipeContextInfo;
  user: BasicUserInfo | null;
}

const logger = createLogger('kemuHubConnector');
const ri = new KemuRemoteInvoke<ArrayBuffer>('kweb');
// @ts-expect-error ignore
ri.setLogger(logger.log);

const _ee = new EventEmitter();
/** keeps custom subscriptions to certain service events */
const _serviceEmitter = new EventEmitter();

let _isGuestMode = false;
let _connectorServiceId: number | null = null;
let _lastServicesList: GetServicesResponse | undefined = undefined;
let _currentRecipePoolId: string | null = null;
let _hubSystemInfo: HubSystemInfo | null = null;
let _userSession: BasicUserInfo | null = null;
type CachedContentsRecord = {
  contents?: ArrayBuffer;
  contentsChecksum: string;
  lastRequestedAt: number;
}

/** 
 * keeps track of the latest service contents so that 
 * widgets can be painted even after services go offline.
 **/
const serviceContentsCache: Record<string, CachedContentsRecord> = {};

const getSession = (): BasicUserInfo | null => {
  if (_userSession) {
    return {
      ..._userSession,
    };
  }

  return null;
};

/**
 * Adds the given service contents to the cache.
 * @param serviceName the name of the service
 * @param version the version of the service
 * @param contents the binary contents to add
 * @returns the newly added record
 */
const createServiceContentsRecord = (serviceName: string, version: string, contents?: ArrayBuffer) => {
  const key = `${serviceName}_${version}`;
  const record = {
    contents,
    lastRequestedAt: Date.now(),
    contentsChecksum: '',
  };

  serviceContentsCache[key] = record;
  return record;
};

const getCachedServiceRecord = (serviceName: string, version: string): CachedContentsRecord | null => {
  const key = `${serviceName}_${version}`;
  const record = serviceContentsCache[key];
  return record || null;
};

const removeCachedServiceContentsRecord = (serviceName: string, version: string) => {
  const key = `${serviceName}_${version}`;
  delete serviceContentsCache[key];
};


const fetchServiceContents = async (serviceName: string, version: string): Promise<GetServiceContentsResponse<ArrayBuffer>[number] | null> => {
  if (!_connectorServiceId) {
    logger.error('Hub Link has not been acknowledged. Cannot get services.');
    return null;
  }

  const request: GetServiceContentsArgs = [{ serviceName, version }];
  const response = await ri.execute<GetServiceContentsResponse<ArrayBuffer>>(
    KemuHubFunctions.GetServiceContents,
    request,
    sendBuffer,
    _connectorServiceId,
    KemuHubServiceId
  );

  return response[0];
};

/** Keeps track of calls to fetch contents */
const fetchContentsPromisesCache = new Map<string, Promise<ArrayBuffer | null>>();

/**
 * Checks the cache for the given service info and returns the last stored contents.
 * If no request has been made for the service, it fetches the contents from the hub
 * and stores it in the cache before returning it. Calling this function multiple times
 * for the same service will only trigger one request to the hub.
 * @param serviceName the name of the service to fetch
 * @param version the version of the service to fetch
 * @param bypassCache if true, the cache will be ignored and the contents will be fetched from the hub
 * regardless of the last request time.
 */
const getServiceContents = async (serviceName?: string, version?: string, bypassCache=false): Promise<ArrayBuffer | null> => {
  if (!serviceName || !version) { return null; }

  // If this method is called multiple times before the first
  // request is resolved, only one request will be made to the hub.
  const cacheKey = `${serviceName}:${version}`;
  if (!bypassCache) {
    const cachedPromise = fetchContentsPromisesCache.get(cacheKey);
    if (cachedPromise) {
      return cachedPromise;
    }
  }

  const record = getCachedServiceRecord(serviceName, version);
  // Attempt to fetch the contents from the hub
  if (!record || bypassCache) {
    const newRecord = record || createServiceContentsRecord(serviceName, version);

    const fetchPromise = (async () => {
      try {
        const response = await fetchServiceContents(serviceName, version);
        if (response) {
          newRecord.contents = response.uiContent;
          newRecord.contentsChecksum = response.uiContentsChecksum || '';
        }
        return newRecord.contents || null;
      } catch (e) {
        logger.error(`Failed to fetch service contents for ${serviceName} v${version}`, e);
        return null;
      } finally {
        // Ensure to remove the promise from the cache once it's settled
        fetchContentsPromisesCache.delete(cacheKey);
      }
    })();

    // Cache the promise before returning it
    fetchContentsPromisesCache.set(cacheKey, fetchPromise);
    return fetchPromise;
  }

  return record?.contents || null;
};

/**
 * Use it as a widget identifier when custom app components use
 * the KL protocol to execute services functions.
 * This replaces the widgetId in the communication protocol.
 */
export const KemuAppIdIdentifier = 'app';

const sendBuffer = kemuLink.sendBuffer;

const onDisconnect = () => {
  logger.log('Disconnected from the server');
  _connectorServiceId = null;
  _hubSystemInfo = null;
  ri.rejectAllPending('Disconnected from the server');
  _ee.emit('disconnected');
};

const onConnected = () => {
  logger.log('Connected to the server');
  _ee.emit('connected');
};

const setCurrentRecipePoolId = (id: string) => {
  _currentRecipePoolId = id;
};

const getRecipePoolId = () => {
  return _currentRecipePoolId;
};

const getEventKey = (
  eventType: 'broadcast' | 'setOutputs',
  serviceName: string,
  serviceVersion: string,
  widgetId: string
) => {
  const eventKey = `${eventType}_${serviceName}_${serviceVersion}_${widgetId}`;
  return eventKey;
};

/**
 * Finds all the HubService widgets in the current recipe
 * and issues subscriptions to their respective services if they are emitters.
 */
const reIssueWidgetsSubscriptions = async () => {
  const currentRecipePoolId = getRecipePoolId();
  if (currentRecipePoolId) {
    const things = findRecipe(currentRecipePoolId)?.blocks;
    if (things) {
      // const hubSubscriptionsToCreate: HubServiceSubscription [] = [];
      // Keeps track of which services have already been subscribed to
      const subscribedMap: Record<string, boolean> = {};

      for (const thingId in things) {
        const thing = things[thingId];
        for (const widgetId in thing.gates) {
          const widget = thing.gates[widgetId];
          if (widget.type === WidgetType.hubService) {
            const widgetState = widget.state as HubServiceState;
            if (widgetState.service?.eventEmitter) {
              const serviceKey = `${widgetState.service.name}_${widgetState.service.version}`;
              const alreadyIssued = subscribedMap[serviceKey];
              if (!alreadyIssued) {
                try {
                  logger.log(`Re-issuing subscription for service ${widgetState.service.name} (${widgetState.service.version})`);
                  // mark the service as subscribed
                  subscribedMap[serviceKey] = true;

                  await subscribeToServiceEvents({
                    listener: {
                      recipeId: currentRecipePoolId,
                    },
                    targetService: {
                      serviceName: widgetState.service.name,
                      version: widgetState.service.version,
                    }
                  });
                } catch (e) {
                  logger.error(`Failed to re-issue subscription for service ${widgetState.service.name} (${widgetState.service.version})`, e);
                }
              }
            }
          }
        }
      }
    }
  }
};

/**
 * Clears the services cache. And fetches the latest list from the hub.
 * Emitting a `services-changed` event to notify all listeners.
 */
const refreshServicesCache = async () => {
  _lastServicesList = undefined;
  // Caches the latest list of services
  const services = await getServices();
  // Notify any subscribers that the services list has changed
  _ee.emit('services-changed', services);
};

/**
 * Called when the hub emits a `services-changed` event.
 * It fetches the latest list of services and updates the cache
 * before notifying any subscribers.
 */
const handleServicesChangedList = async () => {
  logger.log('Services list changed');
  return refreshServicesCache();
};

/**
 * Handles the acknowledgement request from the hub.
 * It sends an ACK response to the hub along with the service id and the api key.
 * @param serviceId the service id that sent the request
 */
const handleAckRequest = async (serviceId?: number) => {
  if (!serviceId) {
    logger.error('Hub sent ACK request without service id');
    return;
  }

  // const ackResponse = buildAckResponse(serviceId, getApiKey());
  const recipePoolId = getRecipePoolId();
  if (!recipePoolId) {
    logger.error('Hub sent ACK request without recipe pool id');
    return;
  }
  
  const recipe = findRecipe(recipePoolId);
  if (!recipe) {
    throw new Error('Hub sent ACK request without recipe');
  }

  const session = getSession();
  const ackResponse: AckResponseArgs = {
    // TODO: remove once all hubs are migrated to version >= 0.7.0, since the `user` field will contain this info.
    // apiKey: session?.apiKey,  // Backwards compatibility 
    recipe: getRecipeContextInfo(recipePoolId),
    user: session,
    ackSessionId: serviceId,
  };

  await ri.execute(KemuHubFunctions.SocketAckResponse, [ackResponse], sendBuffer, serviceId, KemuHubServiceId);

  logger.log('Hub Link acknowledged');
  _connectorServiceId = serviceId;
  _ee.emit('acknowledged');

  await fetchAndCatchHubSystemInfo();
  // Fetch the list of services available on the hub
  // Removed on 21/Jul/2024: Hooks are now responsible for fetching the services list.
  // getServices().catch(e => {
  //   logger.error('Failed to fetch initial services list', e);
  // });

  // Re-issue subscriptions
  reIssueWidgetsSubscriptions();
};

const start = async (config: BasicUserInfo) => {
  // if (!config.apiKey) {
  //   throw new Error('an API key is required to connect to the hub');
  // }
  
  _isGuestMode = false;
  _userSession = config;
  kemuLink.onConnected(onConnected);
  kemuLink.onDisconnected(onDisconnect);
  // Pass any incoming message to the remote invoker for decoding
  kemuLink.onMessageReceived(({ json, transmission }) => {
    ri.processMessage('websocket', sendBuffer, transmission, json);
  });

  // Make sure we terminate any previous connection
  await stop();

  ri.unregisterAllFunctions();
  ri.registerFunction(KemuHubFunctions.SetState, handleSetState);
  ri.registerFunction(KemuHubFunctions.SetOutputs, handleSetOutputs);
  ri.registerFunction(KemuHubFunctions.BroadcastEvent, handleBroadcastEvent);
  ri.registerFunction(KemuHubFunctions.HubBroadcastEvent, handleHubBroadcastEvent);
  ri.registerFunction(KemuHubFunctions.SendToRecipe, handleSendToRecipe);

  ri.registerFunction(ServiceToServiceFunctions.SetDependencyPaths, handleSetDependencyPaths);
  ri.registerFunction(ServiceToServiceFunctions.GetCurrentDependencyPaths, handleGetCurrentDependencyPaths);
  ri.registerFunction(ServiceToServiceFunctions.RemoveDependencyPaths, handleRemoveDependencyPaths);


  ri.registerFunction(ServiceToServiceFunctions.ParseStringExpression, handleParseStringExpression);
  ri.registerFunction(ServiceToServiceFunctions.GetGlobalVariableNames, handleGetGlobalVariableNames);
  ri.registerFunction(ServiceToServiceFunctions.GetGlobalVariableValues, handleGetGlobalVariableValues);
  ri.registerFunction(ServiceToServiceFunctions.SetGlobalVariableValues, handleSetGlobalVariableValues);

  kemuLink.onCommand((command) => {
    // Acknowledgement handling
    onAckRequest(command, handleAckRequest);
    // Services list changed
    onServicesListChanged(command, handleServicesChangedList);
  });

  await kemuLink.connect();
};

/**
 * Connects to the hub without following the normal acknowledgement process.
 * This allows the server to access a limited range of functions, such as requesting file recipe contents.
 * @param config 
 * @returns a promise that resolves when the connection is established
 */
const startInGuestMode = async () => {
  // if (!config.apiKey) {
  //   throw new Error('an API key is required to connect to the hub');
  // }
  
  // Make sure we terminate any previous connection
  await stop();

  _isGuestMode = true;
  kemuLink.onConnected(null);
  kemuLink.onDisconnected(null);
  kemuLink.onCommand(null);

  // Remove any other event handler
  ri.unregisterAllFunctions();

  // Pass any incoming message to the remote invoker for decoding
  kemuLink.onMessageReceived(({ json, transmission }) => {
    ri.processMessage('websocket', sendBuffer, transmission, json);
  });

  await kemuLink.connect();

  const executeGuestHubFunction = <T>(options: { name: string, args: any[], config?: ExecuteConfig}): Promise<T> =>{
    /** executes a function in guest mode */
    return ri.execute<T>(
      options.name,
      options.args,
      sendBuffer,
      KemuComposerGuestServiceId,
      KemuHubServiceId,
      options.config,
    );
  };

  return {
    executeGuestHubFunction,
  }
};

const stop = async () => {
  await kemuLink.disconnect();
};

const isConnected = () => {
  return kemuLink.isConnected();
};

const isReady = () => {
  return !!_connectorServiceId && kemuLink.isConnected();
};

const getStatus = (): HubConnectionStatus => {
  if (_connectorServiceId) { return 'acknowledged'; }
  if (kemuLink.isConnected()) { return 'connected'; }
  return 'disconnected';
};


const handleSendToRecipe: HandleRemoteInvokeFn<ArrayBuffer> = async (event) => {
  const eventArgs = event.args[0] as SendToRecipeArgs;
  const { widgetId, data, recipePoolId, waitForResponse } = eventArgs;
  
  return event.reply({ success: [`message received by recipe ok target widget "${widgetId}", recipe pool id "${recipePoolId}", waitForResponse "${waitForResponse}"`] });
};

/**
 * Handles a broadcast event from the hub.
 */
const handleHubBroadcastEvent: HandleRemoteInvokeFn<ArrayBuffer> = async (event) => {
  const broadcast = event.args[0] as HubBroadcastEvent;
  const broadcastEvent = getEventKey(
    'broadcast',
    `hub_${broadcast.type}`,
    KemuHubServiceId.toString(),
    ''
  );

  await _serviceEmitter.emit(broadcastEvent, broadcast);
};

/**
 * Sends the event to any widget instance that is listening.
 */
const handleBroadcastEvent: HandleRemoteInvokeFn<ArrayBuffer> = async (event) => {
  const broadcast = event.args[0] as BroadcastEvent;
  const currentRecipeId = getRecipePoolId();

  const targetRecipePoolId = broadcast.targetRecipePoolId || broadcast.targetRecipeId;
  // If the target recipe id is provided, only emit the event to the target recipe
  if (targetRecipePoolId && targetRecipePoolId !== currentRecipeId) {
    return;
  }


  // Fix output types
  for (const output of (broadcast.outputs || [])) {
    if (output.type === DataType.ImageData) {
      if (isImageDataType(output.value)) {
        output.value = await createImageDataFromInfo(output.value as ImageDataLike);
      }
    }
  }

  if (currentRecipeId) {
    // Find in all the running things, all widgets that are subscribed to the given service
    const recipe = findRecipe(currentRecipeId);
    if (recipe) {

      /** emits an event using the given target */
      const notifyBroadcast = async (targetId: string) => {
        const broadcastEvent = getEventKey(
          'broadcast',
          broadcast.source.serviceName,
          broadcast.source.serviceVersion,
          targetId,
        );

        try {
          await _serviceEmitter.emit(broadcastEvent, broadcast);
        } catch (e) {
          logger.error(`Failed to emit broadcast event for target ${targetId}`, e);
        }
      };

      // Emit an app broadcast event which would not be tied to a specific widget.
      // This covers cases when specific app components are listening for broadcasts.
      await notifyBroadcast(KemuAppIdIdentifier);

      for (const thingId in recipe.blocks) {
        const thing = recipe.blocks[thingId];
        for (const widgetId in thing.gates) {
          const widget = thing.gates[widgetId];
          if (widget.disabled) { continue; } // Skip disabled widgets

          // Ignore widgets that are not the target widget
          if (broadcast.targetWidgetId && broadcast.targetWidgetId !== widgetId) {
            continue;
          }

          const widgetState = widget.state as HubServiceState;

          // Script Widgets have the ability to handle http broadcast events
          if(widget.type === WidgetType.script){
            await notifyBroadcast(widgetId);
            continue;
          }

          if (
            widget.type === WidgetType.hubService
            && widgetState.service?.name === broadcast.source.serviceName
            && widgetState.service?.version === broadcast.source.serviceVersion
            && widgetState.variantId === broadcast.variantId
          ) {

            // Broadcast to listeners before triggering next gate
            await notifyBroadcast(widgetId);

            // const eventContext: EventContext | undefined = widgetState?.service?.name && broadcast.eventContext ? {
            //   [widgetState.service.name]: broadcast.eventContext
            // } : undefined;

            // Dispatch to outputs
            for (const output of (broadcast.outputs || [])) {
              if (output.value !== null && output.value !== undefined) {
                const data: Data = {
                  type: output.type,
                  value: output.value,
                  timestamp: Date.now(),
                };

                logger.log(`Sending data to output ${output.name} requested by messageId ${event.messageId}`);
                await KemuCore.triggerNextGlobalWidget({
                  recipePoolId: currentRecipeId,
                  currentGateId: widgetId,
                  sourcePortName: output.name,
                  originalEvent: data,
                  data,
                  eventContext: broadcast.eventContext,
                  currentPath: broadcast.currentPath || [],
                  eventId: broadcast.eventId || null,
                });
              }
            }
          }
        }
      }
    }
  }
};

const handleSetOutputs: HandleRemoteInvokeFn<ArrayBuffer> = async (event) => {
  const eventArgs = event.args[0] as SetOutputsArgs<Record<string, unknown>>;
  const { finalState, recipeId, widgetId } = eventArgs;

  // Read the current state from the recipe
  const currentState = KemuCore.getGlobalWidgetState<HubServiceState>(recipeId, widgetId);
  let hubState = currentState as HubServiceState | null;

  // If a new state was provided, update recipe
  if (finalState && hubState && currentState) {
    hubState = {
      ...currentState,
      customState: finalState,
    };

    KemuCore.setGlobalWidgetState(recipeId, widgetId, hubState, true);
  }

  // Dispatch setOutput events interceptions
  if (hubState?.service) {
    try {
      const eventKey = getEventKey(
        'setOutputs',
        hubState.service.name,
        hubState.service.version,
        widgetId
      );
      await _serviceEmitter.emit(eventKey, eventArgs.outputs);
    } catch (e) {
      logger.error(`Failed to emit setOutputs event for widget ${widgetId}`, e);
    }
  }


  let invokingError: Error | null = null;
  // Process the outputs with a non-null value
  for (const output of eventArgs.outputs) {
    if (output.value !== null && output.value !== undefined) {

      if (output.type === DataType.ImageData) {
        // Transform value back into an image data
        if (isImageDataType(output.value)) {
          output.value = await createImageDataFromInfo(output.value as ImageDataLike);
        } else {
          logger.error(`Invalid ImageData value received for output ${output.name}. Expected an ImageData-like object`);
          continue;
        }
      } else if (output.type === DataType.JsonObj || output.type === DataType.Array) {
        // Rebuild the object with the correct types
        await rebuildKemuTypes(output.value);
      }

      const data: Data = {
        type: output.type,
        value: output.value,
        timestamp: Date.now(),
      };

      logger.log(`Sending data to output ${output.name} requested by messageId ${event.messageId}`);

      // const eventContext: EventContext | undefined = hubState?.service?.name && eventArgs.eventContext ? {
      //   [hubState.service.name]: eventArgs.eventContext
      // } : undefined;

      try {
        await KemuCore.triggerNextGlobalWidget({
          recipePoolId: recipeId,
          currentGateId: widgetId,
          sourcePortName: output.name,
          originalEvent: data,
          data,
          eventContext: eventArgs.eventContext,
          currentPath: eventArgs.currentPath || [],
          eventId: eventArgs.eventId || null,
        });
      } catch (e) {
        logger.error(`Failed to trigger next global widget for output ${output.name}`, e);
        invokingError = e;
        break;
      }
    }
  }

  if (invokingError) {
    const isChildWidgetError = invokingError instanceof ChildWidgetError;
    const childWidgetError = invokingError as ChildWidgetError;
    // Propagate the error info back to the service
    event.reply({
      error: invokingError.message,
      errCode: isChildWidgetError ? 'CHILD_WIDGET_ERROR' : 'INVOKING_ERROR',
      errData: {
        targetWidgetId: childWidgetError.targetWidgetId,
        targetPortName: childWidgetError.targetPortName,
        sourcePortName: childWidgetError.sourcePortName,
        sourceWidgetId: childWidgetError.sourceWidgetId,
        targetWidgetType: childWidgetError.targetWidgetType,
        sourceWidgetType: childWidgetError.sourceWidgetType,
      }
    });
  } else {
    event.reply({ success: [] });
  }
};


const handleSetState: HandleRemoteInvokeFn<ArrayBuffer> = async (event) => {
  const eventArgs = event.args[0] as SetStateArgs<Record<string, unknown>>;
  const { newState, recipeId, widgetId } = eventArgs;
  const currentState = KemuCore.getGlobalWidgetState<HubServiceState>(recipeId, widgetId);
  if (currentState) {
    // Services have their own custom state, here we
    // update the portion of the state that is specific to the service
    const finalState: HubServiceState = {
      ...currentState,
      customState: {
        ...currentState.customState,
        ...newState,
      }
    };

    KemuCore.setGlobalWidgetState(recipeId, widgetId, finalState, true);
    return event.reply({ success: [] });
  }
};

/**
 * Invoked when a remote service is trying to register a dependency for a given service instance.
 */
const handleSetDependencyPaths: HandleRemoteInvokeFn<ArrayBuffer> = async ({ args, reply }) => {
  const config = args as SetDependencyPathsArgs;
  const { widgetId, recipeId, paths } = config[0];

  logger.log(`Received request to set a dependency paths for widget "${widgetId}" in recipe "${recipeId}"`);
  const currentState = KemuCore.getGlobalWidgetState<HubServiceState>(recipeId, widgetId);
  if (currentState) {
    const newState: HubServiceState = { ...currentState };

    for (const path of paths) {
      newState.dependencies = newState.dependencies || {};
      newState.dependencies[path.key] = { path: path.path, exclude: path.exclude };
    }

    KemuCore.setGlobalWidgetState(recipeId, widgetId, newState, true);
  }

  return reply({ success: [] });
};

/**
 * Invoked when a remote service is trying to retrieve a dependency path for a given service instance.
 */
const handleGetCurrentDependencyPaths: HandleRemoteInvokeFn<ArrayBuffer> = async ({ args, reply }) => {
  const config = args as GetCurrentDependencyPathsArgs;
  const { recipeId, widgetId } = config[0];

  logger.log(`Received request to get current dependency paths for widget "${widgetId}" in recipe "${recipeId}"`);
  const currentState = KemuCore.getGlobalWidgetState<HubServiceState>(recipeId, widgetId);
  if (currentState) {
    const paths = Object.entries(currentState.dependencies || {}).map(([key, value]) => ({ key, path: value.path, exclude: value.exclude }));
    const response: GetCurrentDependencyPathsResponse = [{ 
      paths
     }];

    logger.log(`Returning dependency paths for widget "${widgetId}"`);
    return reply({ success: response });
  } else {
    return reply({ error: 'Widget not found', errCode: 'WIDGET_NOT_FOUND' });
  }
};

/**
 * Invoked when a remote service is trying to remove a dependency path for a given service instance.
 */
const handleRemoveDependencyPaths: HandleRemoteInvokeFn<ArrayBuffer> = async ({ args, reply }) => {
  const config = args as RemoveDependencyPathsArgs;
  const { recipeId, widgetId, keys } = config[0];
  
  logger.log(`Received request to remove dependency paths for widget "${widgetId}" in recipe "${recipeId}"`);
  const currentState = KemuCore.getGlobalWidgetState<HubServiceState>(recipeId, widgetId);
  if (currentState) {
    const newState: HubServiceState = { ...currentState };
    for (const key of keys) {
      delete newState.dependencies?.[key];
    }
    
    // Update the state
    KemuCore.setGlobalWidgetState(recipeId, widgetId, newState, true);
  }


  return reply({ success: [] });
};


/**
 * Helper method to get the recipe info from the recipe cache
 * @param recipePoolId 
 * @returns the recipe info
 * @throws an error if the recipe does not exist
 */
const getRecipeInfo = (recipePoolId: string) => {
	const recipe = findRecipe(recipePoolId);
	if (!recipe) {
		throw new Error(`Recipe [${recipePoolId} does not exist]`);
	}

	// First we try to use the db id, if it exists
	return {
		uuid: recipe.uuid,
		recipeType: recipe.dbInfo?.recipeType,
	};
};

/**
 * Allows services to parse string expressions using kemu-core's 
 * stringExpressionsManager with access to global variables.
 */
const handleParseStringExpression: HandleRemoteInvokeFn<ArrayBuffer> = async ({ args, reply }) => {
  const config = args[0] as ParseStringExpressionArgs[0];
  
  if(!config.expressions) {
    return reply({ error: 'Expression is required', errCode: 'EXPRESSION_REQUIRED' });
  }

  const widget = findWidget({ recipeId: config.recipePoolId, widgetId: config.widgetId });
  if (!widget) {
    return reply({ error: 'Widget not found', errCode: 'WIDGET_NOT_FOUND' });
  }

  if(widget.type !== WidgetType.hubService) {
    return reply({ error: 'Widget is not a hub service', errCode: 'WIDGET_NOT_A_HUB_SERVICE' });
  }

  const serviceWidgetState = widget.state as HubServiceState;
  const { uuid } = getRecipeInfo(config.recipePoolId);

  const results = await Promise.all(config.expressions.map(async (expression) => {
    // TODO: figure out a way to allow giving services permissions to run custom functions
    const result = await renderTemplateAsync({
      template: expression.text,
      context: expression.context || {},
      options: {
        enableFnTag: false,
        recipeUuid: uuid,
        serviceName: serviceWidgetState.service?.name,
        securityOptions: {
          restrictionLevel: 'strict',
        }
      }
    }); 

    return result;
  }));

  const response: ParseStringExpressionResponse = [{
    expressions: results,
  }];

  return reply({ success: response });
};

/**
 * Retrieves the list of all global variable names and their types.
 */
const handleGetGlobalVariableNames: HandleRemoteInvokeFn<ArrayBuffer> = async ({ args, reply }) => {
  const config = args[0] as GetGlobalVariableNamesArgs[0];
  const variables = globalVariablesManager.getAllVariables();
  const filteredVars = variables.filter((variable) => 
    !config.filterTypes 
    || config.filterTypes.includes(variable.controlType)
  ).map((variable) => ({ name: variable.name, type: variable.controlType }));

  const response: GetGlobalVariableNamesResponse = [{
    variables: filteredVars,
  }];

  return reply({ success: response });
};

/**
 * Retrieves the values of a list of global variables.
 */
const handleGetGlobalVariableValues: HandleRemoteInvokeFn<ArrayBuffer> = async ({ args, reply }) => {
  const config = args[0] as GetGlobalVariableValuesArgs[0];
  if(!config.names || config.names.length === 0) {
    return reply({ error: 'Variable names are required', errCode: 'VARIABLE_NAMES_REQUIRED' });
  }

  const variables = globalVariablesManager.getAllVariables();
  const filteredVars = variables.filter((variable) => config.names.includes(variable.name))
    .map((variable) => ({ name: variable.name, value: variable.lastValue }));

  const response: GetGlobalVariableValuesResponse = [{
    values: filteredVars,
  }];

  return reply({ success: response });
};

/**
 * Sets the values of a list of global variables.
 */
const handleSetGlobalVariableValues: HandleRemoteInvokeFn<ArrayBuffer> = async ({ args, reply }) => {
  const config = args[0] as SetGlobalVariableValuesArgs[0];
  if(!config.values || config.values.length === 0) {
    return reply({ error: 'Variable values are required', errCode: 'VARIABLE_VALUES_REQUIRED' });
  }

  try {
    const { values } = config;
    for (const value of values) {
      await globalVariablesManager.setGlobalVariable({
        name: value.name,
        value: value.value,
      });
    }
  
    return reply({ success: [] });
  }catch(e) {
    logger.error(`Failed to set global variable values: ${e}`);
    return reply({ error: 'Failed to set some of the global variable values', errCode: 'FAILED_TO_SET_GLOBAL_VARIABLE_VALUES', errData: e.message });
  }
};

/**
 * Fetches the current list of services from the hub.
 * @returns the latest list of services available on the hub.
 */
const getServices = async (): Promise<GetServicesResponse> => {
  logger.log(`Requesting services on ${new Date().toISOString()}`);

  if (!_connectorServiceId) {
    logger.error('Hub Link has not been acknowledged. Cannot get services.');
    return {
      available: [],
      installed: [],
      failed: [],
    };
  }

  const [response] = await ri.execute<[GetServicesResponse]>(
    KemuHubFunctions.GetServices,
    [],
    sendBuffer,
    _connectorServiceId,
    KemuHubServiceId
  );

  // Cache the list for faster look up
  _lastServicesList = response;

  // Check if any service has change its contents and if so, clear out the cache
  for (const service of response.available) {
    // Ignore internal services
    if (service.internal) { continue; }
    const record = getCachedServiceRecord(service.name, service.version);
    if (!record || record?.contentsChecksum !== service.uiContentChecksum) {
      try {
        // Add record if missing
        const targetRecord = record || createServiceContentsRecord(service.name, service.version);
        logger.log(`Service ${service.name} v${service.version} contents have changed, fetching new contents`);
        const newContents = await fetchServiceContents(service.name, service.version);
        if (newContents) {
          targetRecord.contents = newContents.uiContent;
          targetRecord.contentsChecksum = service.uiContentChecksum || '';
        }
      } catch (e) {
        logger.error(`Failed to update service contents for ${service.name} v${service.version}`, e);
      }
    }
  }
  return response;
};



/**
 * @returns a copy of the last services list fetched from the hub.
 */
const getCachedServices = () => {
  return _lastServicesList || {
    available: [],
    installed: [],
    failed: [],
  };
};

/**
 * @returns true if the services list has been cached at least once.
 */
const areServicesCached = () => {
  return !!_lastServicesList;
};


const onParentEvent: ParentEventHandler = async (event) => {
  const { targetServiceSessionId } = event;

  if (!_connectorServiceId) {
    logger.error('No target service session id provided');
    return;
  }

  logger.log(`Forwarding "onParentEvent" to service ${targetServiceSessionId}`);
  const result = await ri.execute(
    KemuHubFunctions.OnParentEvent,
    [event],
    sendBuffer,
    _connectorServiceId,
    targetServiceSessionId,
    event.config,
  ).catch((e) => {
    logger.error(`Error invoking onParentEvent callback: ${e}`);
    if (e.errCode === RemoveInvokeError.ParentEventCallbackError) {
      throw e.error;
    } else {
      throw e;
    }
  });

  return result;
};

/* const getInputs: GetInputNamesHandler = async (serviceSessionId, environment) => {
  if (!connectorServiceId) {
    logger.error('Hub Link has not been acknowledged. Cannot get services.');
    return [];
  }

  const getInputsArgs: GetInputsArgs = [environment];

  const inputs = await ri.execute<WidgetPort[]>(
    KemuHubFunctions.GetInputs,
    getInputsArgs,
    sendBuffer,
    connectorServiceId,
    serviceSessionId,
  );

  // Translate DataTypeStr into DataType
  // const fixedInputs = inputs.map((input) => {
  //   const types = Array.isArray(input.type) ? input.type : [input.type];
  //   const numericTypes = types.map((t) => DataType[t]);
  //   return {
  //     ...input,
  //     type: numericTypes,
  //   };
  // });

  return inputs;
}; */

/* const getOutputs: GetOutputNamesHandler = async (serviceSessionId: number, environment) => {
  if (!connectorServiceId) {
    logger.error('Hub Link has not been acknowledged. Cannot get services.');
    return [];
  }

  const getOutputsArgs: GetOutputsArgs = [environment];

  const outputs = await ri.execute<WidgetPort[]>(
    KemuHubFunctions.GetOutputs,
    getOutputsArgs,
    sendBuffer,
    connectorServiceId,
    serviceSessionId,
  );

  // Translate DataTypeStr into DataType
  // const fixedOutputs = outputs.map((output) => {
  //   const types = Array.isArray(output.type) ? output.type : [output.type];
  //   const numericTypes = types.map((t) => DataType[t]);
  //   return {
  //     ...output,
  //     type: numericTypes,
  //   };
  // });

  return outputs;
}; */

const getDefaultState: GetDefaultStateHandler = async <T extends WidgetState>(serviceSessionId: number): Promise<T> => {
  if (!_connectorServiceId) {
    logger.error('Hub Link has not been acknowledged. Cannot get services.');
    return {} as T;
  }

  const [serviceState] = await ri.execute<[T]>(
    KemuHubFunctions.GetDefaultState,
    [],
    sendBuffer,
    _connectorServiceId,
    serviceSessionId,
  );

  return serviceState;
};

const callProcessorHandler: CallProcessorFuncHandler = async (
  serviceSessionId,
  environment,
  name,
  data,
  config
) => {
  if (!_connectorServiceId) {
    logger.error('Hub Link has not been acknowledged. Cannot get services.');
    return null;
  }

  const [response] = await ri.execute(
    KemuHubFunctions.UIEvent,
    [environment, name, data],
    sendBuffer,
    _connectorServiceId,
    serviceSessionId,
    config,
  );

  return response;
};

const onCommand = (command: SubscribableHubCommand, cb: VoidCallback) => {
  const handler = (...args: any[]) => {
    cb(...args);
  };

  _ee.on(command, handler);

  return () => _ee.off(command, handler);
};

const subscribeToServiceEvents: SubscribeToServiceEventsHandler = async (subscription) => {
  if (!_connectorServiceId) {
    logger.error('Hub Link has not been acknowledged. Cannot subscribe to services.');
    return;
  }

  return ri.execute(
    KemuHubFunctions.SubscribeToService,
    [subscription],
    sendBuffer,
    _connectorServiceId,
    KemuHubServiceId,
  );
};

const unsubscribeFromServiceEvents: UnsubscribeFromServiceHandler = async (subscription) => {
  if (!_connectorServiceId) {
    logger.error('Hub Link has not been acknowledged. Cannot subscribe to services.');
    return;
  }

  return ri.execute(
    KemuHubFunctions.UnsubscribeFromService,
    [subscription],
    sendBuffer,
    _connectorServiceId,
    KemuHubServiceId,
  );
};

const onBroadcastEvent = <T extends BroadcastEvent | HubBroadcastEvent = BroadcastEvent>(
  widgetId: string,
  serviceName: string,
  serviceVersion: string,
  cb: (event: DeepReadOnly<T>) => void | Promise<void>
) => {
  const eventKey = getEventKey('broadcast', serviceName, serviceVersion, widgetId);
  const unsubscribe = _serviceEmitter.on(eventKey, cb);
  return unsubscribe;
};

const onSetOutputsEvent = (
  widgetId: string,
  serviceName: string,
  serviceVersion: string,
  cb: (outputs: DeepReadOnlyArray<TargetOutput>) => void | Promise<void>
) => {
  const eventKey = getEventKey('setOutputs', serviceName, serviceVersion, widgetId);
  const unsubscribe = _serviceEmitter.on(eventKey, cb);
  return unsubscribe;
};


// eslint-disable-next-line @typescript-eslint/no-explicit-any
const executeHubFunction = async <T>(name: string, args: any[], config?: ExecuteConfig): Promise<T | null> => {
  if (!_connectorServiceId) {
    logger.error('Hub Link has not been acknowledged. Cannot execute function.');
    return null;
  }

  return ri.execute<T>(
    name,
    args,
    sendBuffer,
    _connectorServiceId,
    KemuHubServiceId,
    config,
  );
};

const initializeServiceInstance: InitializeServiceInstanceHandler = async (config) => {
  if (!_connectorServiceId) {
    logger.error('Hub Link has not been acknowledged. Cannot execute function.');
    return;
  }

  const widgetState = KemuCore.getGlobalWidgetState<HubServiceState>(config.recipePoolId, config.widgetId);
  if (!widgetState) {
    throw new Error('Widget or recipe pool not found. Cannot initialize service instance.');
  }

  // const session = getSession();
  const depKeys = Object.keys(widgetState.dependencies || {});
  const dependenciesMap = depKeys.reduce((acc, key) => {
    acc[key] = widgetState.dependencies?.[key]?.path || null;
    return acc;
  }, {} as Record<string, string | null>);

  const recipe = findRecipe(config.recipePoolId);
  if (!recipe) {
    throw new Error('Recipe not found. Cannot initialize service instance.');
  }
  
  const initArgs: InitInstanceArgs = [{
    currentState: widgetState.customState,
    recipeId: config.recipePoolId,
    recipe: getRecipeContextInfo(config.recipePoolId),
    widgetId: config.widgetId,
    variantId: config.variantId,
    recipeType: config.recipeType,
    recipeName: config.recipeName,
    currentDependencies: dependenciesMap,
    // kemuApiKey: session?.apiKey,
  }];

  const [updatedState, requestedSecrets, declaredGlobalVars] = await ri.execute<InitInstanceResponse>(
    KemuHubFunctions.InitializeInstance,
    initArgs,
    sendBuffer,
    _connectorServiceId,
    config.sessionId,
  );

  if(requestedSecrets && requestedSecrets.length > 0) {
    // sanitize the requested secrets
    const validSecrets: WidgetSecretConfig[] = requestedSecrets
      .filter((secret) => secret.name && secret.name.length > 0)
      .map((secret) => ({
        ...secret,
        hubServiceName: widgetState.service?.name,
      }));

    KemuCore.registerWidgetSecrets({
      clearExistingSecrets: true,
      recipePoolId: config.recipePoolId,
      widgetId: config.widgetId,
      secrets: validSecrets,
    });
  }

  if (declaredGlobalVars && declaredGlobalVars.length > 0) {
    // register the declared global variables
    await Promise.all(declaredGlobalVars.map(async (variable) => {
      await globalVariablesManager.setGlobalVariable({
        name: variable.name,
        value: variable.value,
        controlType: variable.controlType,
        controlSettings: variable.controlSettings,
        helpText: variable.helpText,
        order: variable.order,
        disableChangeNotifications: variable.emitChangeEvent === false,
        disableDefineNotifications: variable.emitDefineEvent === false,
        doNotOverrideExisting: variable.doNotOverrideExisting ?? false,
        widgetId: config.widgetId,
      });
    }));
  }

  if (updatedState) {
    // Read the current state from the recipe in case it was updated while the call was in progress
    const widgetState = KemuCore.getGlobalWidgetState<HubServiceState>(config.recipePoolId, config.widgetId);
    if (widgetState) {
      const finalState: HubServiceState = {
        ...widgetState,
        customState: updatedState,
      };

      KemuCore.setGlobalWidgetState(config.recipePoolId, config.widgetId, finalState, true);
    }

  }
};

const terminateServiceInstance: TerminateServiceInstanceHandler = async (
  serviceSessionId, widgetId, recipePoolId, variantId,
) => {
  if (!_connectorServiceId) {
    logger.error('Hub Link has not been acknowledged. Cannot execute function.');
    return;
  }

  const widgetState = KemuCore.getGlobalWidgetState<HubServiceState>(recipePoolId, widgetId);
  if (!widgetState) {
    throw new Error('Widget or recipe pool not found. Cannot initialize service instance.');
  }

  const recipe = findRecipe(recipePoolId);
  if (!recipe) {
    throw new Error('Recipe not found. Cannot terminate service instance.');
  }

  const endArgs: TerminateInstanceArgs = [{
    currentState: widgetState.customState,
    recipeId: recipePoolId,
    recipe: {
      poolId: recipePoolId,
      uuid: recipe.uuid,
      name: recipe.name,
      type: recipe.dbInfo.recipeType,
      dbId: recipe.dbInfo.id,
      version: recipe.dbInfo.version,
      authorId: recipe.dbInfo.authorId,
    },
    widgetId,
    variantId,
  }];

  await ri.execute<[HubServiceState | null]>(
    KemuHubFunctions.TerminateInstance,
    endArgs,
    sendBuffer,
    _connectorServiceId,
    serviceSessionId,
  );
};

/**
   * Finds a service that matches the given name and compatible version.
   * @param name the name of the version to look for
   * @param targetVersion the minimum version to look for
   * @param compareMode the comparison mode to use when comparing versions. Defaults to '^`, which matches
   * patch and minor versions.
   * @param bypassCache if true, the current service list will be fetched from the hub instead of the cache.
   * If cache is not available, the services list will be fetched from the hub as well regardless of this flag.
   * @returns the service info if found, null otherwise.
   */
const getCompatibleService = async (name: string, targetVersion: string, compareMode: VersionCompareMode = '^', bypassCache?: boolean): Promise<SerializableServiceInfo | null> => {
  const validModes: VersionCompareMode[] = ['^', '~', '>=', '>', '<=', '<', '='];
  if (!validModes.includes(compareMode)) {
    throw new Error(`Invalid compare mode "${compareMode}". Must be one of ${validModes.join(', ')}`);
  }

  const useCache = areServicesCached();
  const services = (bypassCache || !useCache) ? await getServices() : getCachedServices();
  for (const service of services.available) {
    if (service.name === name) {
      // Exact match
      if (service.version === targetVersion) { return service; }
      // Semantic versioning match
      const isMatch = satisfies(service.version, `${compareMode}${targetVersion}`);
      if (isMatch) { return service; }
    }
  }


  return null;
};

/**
 * Fetches the system info from the hub.
 * @param noCache if true, the system info will be fetched from the hub regardless of whether it is cached or not.
 * @returns the system info if successful, null otherwise.
 */
const getHubSystemInfo = async (noCache: boolean = false): Promise<HubSystemInfo | null> => {
  if (!_connectorServiceId) {
    logger.warn('Hub Link has not been acknowledged. Cannot get services.');
    return null;
  }

  if (_hubSystemInfo && !noCache) {
    return _hubSystemInfo;
  }

  const [systemInfo] = await ri.execute<[HubSystemInfo]>(
    KemuHubFunctions.GetSystemInfo,
    [],
    sendBuffer,
    _connectorServiceId,
    KemuHubServiceId,
  ).catch((error) => {
    logger.error('Failed to get system info', error);
    return [null];
  });

  return systemInfo;
};

/**
 * Fetches the system info from the hub and caches it.
 */
const fetchAndCatchHubSystemInfo = async () => {
  _hubSystemInfo = await getHubSystemInfo();
};

const getConnector = () => {
  const hubConnector: HubConnectorWithWebAppMethods = {
    getServices,
    getServiceContents,
    onParentEvent,
    // getInputs,
    // getOutputs,
    onCommand,
    getDefaultState,
    getCachedServices,
    areServicesCached,
    subscribeToServiceEvents,
    unsubscribeFromServiceEvents,
    callProcessorHandler,
    onBroadcastEvent,
    onSetOutputsEvent,
    executeHubFunction,
    initializeServiceInstance,
    terminateServiceInstance,
    getCompatibleService,
    getHubSystemInfo,
  };

  return hubConnector;
};


/**
 * Registers a callback function to be called when the specified event occurs.
 * @param event The event to listen for. Can be either 'connected' or 'disconnected'.
 * @param cb The callback function to be called when the event occurs.
 */
const on = (event:  ValidEvent, cb: VoidCallback ) => {
  _ee.on(event, cb);
};

/**
 * Removes a previously registered callback function from the specified event.
 * @param event The event to remove the callback from. Can be either 'connected' or 'disconnected'.
 * @param cb The original callback function used when registering the event.
 */
const off = (event:  ValidEvent, cb: VoidCallback ) => {
  _ee.off(event, cb);
};


export default {
  start,
  startInGuestMode,
  stop,
  on,
  off,
  isConnected,
  getConnector,
  isReady,
  getStatus,
  setCurrentRecipePoolId,
  reIssueWidgetsSubscriptions,
  refreshServicesCache,
};
